import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Users, DollarSign, TrendingUp, Copy, ExternalLink, 
  Loader2, Gift, Award, Star, Medal, Crown, ChevronRight,
  Calendar, CreditCard, ArrowUpRight
} from 'lucide-react';
import api from '@/services/api';

const TIER_CONFIG = {
  bronze: { icon: Medal, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  silver: { icon: Award, color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200' },
  gold: { icon: Star, color: 'text-yellow-500', bg: 'bg-yellow-50', border: 'border-yellow-200' },
  platinum: { icon: Crown, color: 'text-purple-500', bg: 'bg-purple-50', border: 'border-purple-200' }
};

export const AffiliateDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [referralLink, setReferralLink] = useState('');
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [dashRes, linkRes] = await Promise.all([
        api.get('/affiliate/dashboard'),
        api.get('/affiliate/referral-link')
      ]);
      setDashboard(dashRes.data);
      setReferralLink(linkRes.data.referral_link);
    } catch (error) {
      console.error('Failed to load affiliate dashboard:', error);
      toast.error('Failed to load affiliate data');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied to clipboard!');
  };

  const handleRequestPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setRequestingPayout(true);
    try {
      await api.post('/affiliate/request-payout', { amount, payout_method: 'stripe' });
      toast.success('Payout request submitted successfully!');
      setPayoutAmount('');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!dashboard?.program_enabled) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="max-w-md bg-white border-slate-200">
          <CardContent className="pt-6 text-center">
            <Gift className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Affiliate Program Unavailable</h2>
            <p className="text-slate-600">The affiliate program is currently disabled. Please check back later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { profile, stats, referrals, recent_commissions, tiers } = dashboard;
  const tierConfig = TIER_CONFIG[profile?.tier || 'bronze'];
  const TierIcon = tierConfig.icon;

  return (
    <div className="space-y-8" data-testid="affiliate-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Affiliate Program</h1>
          <p className="text-slate-600 mt-1">Earn money by referring others to Harmonix</p>
        </div>
        <Badge className={`${tierConfig.bg} ${tierConfig.color} ${tierConfig.border} border text-base px-4 py-2`}>
          <TierIcon className="w-5 h-5 mr-2" />
          {profile?.tier_info?.name || 'Bronze'} Tier
        </Badge>
      </div>

      {/* Payout terms — set expectations up-front so affiliates don't
          feel misled about when/why commissions post. */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="affiliate-payout-terms">
        <p className="text-sm font-semibold text-amber-900">How you get paid</p>
        <p className="text-sm text-amber-800 mt-1 leading-relaxed">
          Commissions accrue only for referred users who <strong>become and remain paying subscribers</strong>. Your share is calculated on invoices that Harmonix has actually collected — refunds, chargebacks, and cancellations reverse the associated commission. This keeps the program sustainable and honest for everyone.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          label="Total Referrals" 
          value={stats?.total_referrals || 0} 
          icon={Users} 
          color="blue" 
        />
        <StatCard 
          label="Commission Rate" 
          value={`${stats?.commission_rate || 10}%`} 
          icon={TrendingUp} 
          color="emerald" 
        />
        <StatCard 
          label="Total Earned" 
          value={`$${(stats?.total_earned || 0).toFixed(2)}`} 
          icon={DollarSign} 
          color="green" 
        />
        <StatCard 
          label="Pending Payout" 
          value={`$${(stats?.pending_amount || 0).toFixed(2)}`} 
          icon={CreditCard} 
          color="amber" 
        />
      </div>

      {/* Referral Link */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Gift className="w-5 h-5 text-blue-600" />
            Your Referral Link
          </CardTitle>
          <CardDescription className="text-slate-600">
            Share this link to earn {stats?.commission_rate || 10}% commission on every payment from users you refer
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input 
              value={referralLink} 
              readOnly 
              className="bg-white border-blue-200 text-slate-900 font-mono text-sm"
              data-testid="referral-link-input"
            />
            <Button onClick={copyLink} className="bg-blue-600 hover:bg-blue-700" data-testid="copy-link-btn">
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
            <Button 
              variant="outline" 
              className="border-blue-300 text-blue-700"
              onClick={() => window.open(referralLink, '_blank')}
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Tier Progress */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-600" />
              Tier Progress
            </CardTitle>
            <CardDescription className="text-slate-600">
              Get more referrals to unlock higher commission rates
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(tiers || {}).map(([key, tier]) => {
              const isCurrentTier = key === profile?.tier;
              const isUnlocked = (stats?.total_referrals || 0) >= tier.min_referrals;
              const TIcon = TIER_CONFIG[key]?.icon || Medal;
              
              return (
                <div 
                  key={key}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isCurrentTier 
                      ? `${TIER_CONFIG[key].bg} ${TIER_CONFIG[key].border}` 
                      : isUnlocked 
                        ? 'bg-slate-50 border-slate-200' 
                        : 'bg-slate-50/50 border-dashed border-slate-200 opacity-60'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <TIcon className={`w-6 h-6 ${TIER_CONFIG[key].color}`} />
                      <div>
                        <p className="font-semibold text-slate-900">{tier.name}</p>
                        <p className="text-sm text-slate-500">
                          {tier.min_referrals === 0 ? 'Starting tier' : `${tier.min_referrals}+ referrals`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-emerald-600">{tier.commission_rate}%</p>
                      <p className="text-xs text-slate-500">commission</p>
                    </div>
                  </div>
                  {isCurrentTier && (
                    <Badge className="mt-2 bg-blue-600">Current Tier</Badge>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Request Payout */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-600" />
              Request Payout
            </CardTitle>
            <CardDescription className="text-slate-600">
              Withdraw your pending earnings (minimum $50)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-700">Available Balance</p>
              <p className="text-3xl font-bold text-emerald-700">
                ${(stats?.pending_amount || 0).toFixed(2)}
              </p>
            </div>
            
            <div className="flex gap-2">
              <Input 
                type="number"
                placeholder="Enter amount"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                className="bg-white border-slate-300 text-slate-900"
                min="50"
                max={stats?.pending_amount || 0}
                data-testid="payout-amount-input"
              />
              <Button 
                onClick={handleRequestPayout}
                disabled={requestingPayout || (stats?.pending_amount || 0) < 50}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="request-payout-btn"
              >
                {requestingPayout ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>Request</>
                )}
              </Button>
            </div>
            
            {(stats?.pending_amount || 0) < 50 && (
              <p className="text-sm text-amber-600">
                Minimum payout is $50. You need ${(50 - (stats?.pending_amount || 0)).toFixed(2)} more.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Referrals */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Recent Referrals
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!referrals || referrals.length === 0) ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No referrals yet</p>
                <p className="text-sm">Share your link to start earning!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {referrals.slice(0, 5).map((ref, i) => (
                  <div key={ref.id || ref.user_id || `referral-${i}-${ref.name || ref.email || ''}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">{ref.name || 'User'}</p>
                      <p className="text-sm text-slate-500">{ref.email}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-xs">
                        {ref.subscription?.tier || 'free'}
                      </Badge>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(ref.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Commissions */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Recent Commissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!recent_commissions || recent_commissions.length === 0) ? (
              <div className="text-center py-8 text-slate-500">
                <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No commissions yet</p>
                <p className="text-sm">Earn when your referrals subscribe!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recent_commissions.slice(0, 5).map((comm, i) => (
                  <div key={comm.id || comm.commission_id || `commission-${i}-${comm.created_at || comm.amount || ''}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-emerald-600">+${comm.amount?.toFixed(2)}</p>
                      <p className="text-xs text-slate-500">
                        {comm.commission_rate}% of ${comm.payment_amount?.toFixed(2)}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className={
                        comm.status === 'paid' ? 'bg-green-100 text-green-700' : 
                        comm.status === 'pending' ? 'bg-amber-100 text-amber-700' : 
                        'bg-slate-100 text-slate-700'
                      }>
                        {comm.status}
                      </Badge>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(comm.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-600',
    green: 'bg-green-50 border-green-200 text-green-600',
    amber: 'bg-amber-50 border-amber-200 text-amber-600',
    purple: 'bg-purple-50 border-purple-200 text-purple-600'
  };

  return (
    <div className={`p-4 rounded-lg border ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
};

export default AffiliateDashboard;
